import React, { useMemo, useState, useEffect } from 'react';
import { Card, DatePicker, Radio, Select, Space, Table, Typography, Empty, Tag, Image, Button, Modal, message } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import { useQuery } from 'react-query';
import { useAuthStore } from '../../stores/authStore';
import { DownloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * 日志统计页面（数据分析子页面）
 * 功能说明：
 * 1. 按日期统计：展示全体设计人员在某一天的日志情况；若某位设计师当天未提交日志，则以红色“空缺”标识。
 * 2. 按设计师统计：选择一个设计师并指定日期范围，展示该设计师在区间内的日志明细列表。
 * 技术要点：
 * - 复用现有 /users 与 /daily-logs 接口；按日期模式下对用户列表与日志列表做前端聚合，补充“空缺”状态。
 * - 使用 react-query 进行数据请求与状态管理。
 */
// 日志统计页面组件
// 功能：提供按日期、按设计师、按月份的日志统计视图；顶部标题与说明已移除
export default function LogStats() {
  const { isAuthenticated } = useAuthStore();

  // 视图模式：按日期 | 按设计师 | 按月统计
  const [mode, setMode] = useState('byDate'); // 'byDate' | 'byUser' | 'byMonth'

  // 按日期模式所选日期
  const [selectedDate, setSelectedDate] = useState(dayjs());

  // 按设计师模式：选择用户 + 日期范围
  const [selectedUserId, setSelectedUserId] = useState(undefined);
  const [dateRange, setDateRange] = useState([dayjs().subtract(6, 'day'), dayjs()]);
  // Shift键与动画状态
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [animateSelect, setAnimateSelect] = useState(false);

  // 按月统计模式：选择月份
  const [selectedMonth, setSelectedMonth] = useState(dayjs());

  // 日志统计表导出弹窗状态
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportMonth, setExportMonth] = useState(dayjs());
  const [exporting, setExporting] = useState(false);

  /**
   * 切换到按设计师统计视图
   * 根据来源（按日期/按月）设置合适的默认日期范围，并选中点击的设计师
   * @param {number} userId 设计师用户ID
   * @param {('byDate'|'byMonth')} source 来源视图标识
   */
  const handleSwitchToUser = (userId, source) => {
    setSelectedUserId(userId);
    // 默认日期范围：最近10天（含结束日）
    // 若从“按日期”视图进入，则以当前选中日期为结束日；否则以今天为结束日
    const endDay = (source === 'byDate' && selectedDate) ? selectedDate : dayjs();
    const startDay = endDay.clone().subtract(9, 'day');
    setDateRange([startDay, endDay]);
    setMode('byUser');
  };

  // 用户列表（用于识别设计类角色）
  const { data: usersResp, isLoading: loadingUsers } = useQuery(
    ['users'],
    async () => {
      // 中文函数级注释：为避免默认分页导致只加载前N个用户，这里显式请求较大的limit以获取完整列表
      const res = await axios.get('/users', { params: { page: 1, limit: 1000 } });
      return res.data?.data || {};
    },
    { enabled: isAuthenticated }
  );

  const designers = useMemo(() => {
    const users = usersResp?.users || [];
    // 仅展示设计类角色（与盈亏统计页面保持一致：designer/manager）
    return users.filter(u => ['designer', 'manager'].includes(u.role));
  }, [usersResp]);

  /**
   * 函数：buildDesignerIds 构建可循环的设计师ID列表
   * 说明：从设计师列表中提取ID，并按真实姓名排序以获得稳定遍历顺序
   */
  const designerIds = useMemo(() => {
    const arr = (designers || []).map(d => ({ id: d.id, name: d.realName || '' }));
    arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr.map(x => x.id);
  }, [designers]);

  /**
   * 函数：shouldExcludePosition 判断职位是否需要在导出时排除
   * 说明：不导出“总监”“各部门经理（职位名称中包含经理）”“系统管理员”相关记录
   */
  const shouldExcludePosition = (position) => {
    if (!position) return false;
    if (position.includes('总监')) return true;
    if (position.includes('系统管理员')) return true;
    if (position.includes('经理')) return true;
    return false;
  };

  /**
   * 函数：buildMonthlyExportData 根据整月日志构建按职位分组的每人日志数量统计
   * 说明：先按用户聚合日志数量，再按职位拆分为多个职位分组
   */
  const buildMonthlyExportData = (logs, monthStr) => {
    const perUserMap = new Map();
    (logs || []).forEach((log) => {
      const user = log.user;
      if (!user) return;
      const position = user.position || '';
      if (shouldExcludePosition(position)) return;
      const uid = user.id;
      if (!perUserMap.has(uid)) {
        perUserMap.set(uid, {
          userId: uid,
          realName: user.realName || '',
          position,
          logCount: 0,
          month: monthStr
        });
      }
      const entry = perUserMap.get(uid);
      entry.logCount += 1;
    });

    const byPositionMap = new Map();
    Array.from(perUserMap.values()).forEach((entry) => {
      const key = entry.position || '未设置职位';
      if (!byPositionMap.has(key)) {
        byPositionMap.set(key, []);
      }
      byPositionMap.get(key).push(entry);
    });

    return byPositionMap;
  };

  /**
   * 函数：isLeaveLog 判断单条日志是否为调休日志
   * 说明：通过 todayWork 与 workHours 字段识别系统自动创建的调休日志
   */
  const isLeaveLog = (log) => {
    if (!log) return false;
    const text = log.todayWork || '';
    const hours = Number(log.workHours || 0);
    return text.includes('调休') && hours === 0;
  };

  /**
   * 函数：getDailyStatusFromLogs 根据日志列表计算当天状态
   * 说明：若无日志则为空缺；若全部为调休则为调休；否则视为有日志
   */
  const getDailyStatusFromLogs = (logs = []) => {
    if (!logs.length) return '空缺';
    const hasNonLeave = logs.some(l => !isLeaveLog(l));
    if (!hasNonLeave) return '调休';
    return '有日志';
  };

  /**
   * 函数：exportPositionCsv 为单个职位生成CSV并触发浏览器下载
   * 说明：文件命名格式为“日志统计_YYYY-MM_职位.csv”
   */
  const exportPositionCsv = (position, list, monthStr) => {
    if (!list || !list.length) return;
    const headers = ['姓名', '职位', '月份', '日志数量'];
    const escapeCell = (value) => {
      const str = value === undefined || value === null ? '' : String(value);
      if (str.includes('"') || str.includes(',') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const lines = [
      headers.join(',')
    ];
    list.forEach((item) => {
      lines.push([
        escapeCell(item.realName),
        escapeCell(item.position || ''),
        escapeCell(monthStr),
        escapeCell(item.logCount)
      ].join(','));
    });
    const csvContent = '\uFEFF' + lines.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `日志统计_${monthStr}_${position || '未设置职位'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * 函数：handleOpenExportModal 打开日志统计表导出弹窗
   * 说明：默认选中当前“按月统计”视图的月份；若未进入该视图则默认当前月份
   */
  const handleOpenExportModal = () => {
    const baseMonth = selectedMonth || dayjs();
    setExportMonth(baseMonth);
    setExportModalVisible(true);
  };

  /**
   * 函数：handleConfirmExport 确认导出所选月份的日志统计表
   * 说明：请求整月日志数据，按职位拆分为多个CSV文件并逐个触发下载
   */
  const handleConfirmExport = async () => {
    if (!exportMonth) {
      message.warning('请选择导出月份');
      return;
    }
    try {
      setExporting(true);
      const monthStr = exportMonth.format('YYYY-MM');
      const startDate = exportMonth.startOf('month').format('YYYY-MM-DD');
      const endDate = exportMonth.endOf('month').format('YYYY-MM-DD');
      const res = await axios.get('/daily-logs', {
        params: {
          startDate,
          endDate,
          page: 1,
          limit: 20000
        }
      });
      const logs = res.data?.data?.logs || [];
      if (!logs.length) {
        message.info('该月份暂无任何日志数据，无法导出');
        return;
      }
      const byPositionMap = buildMonthlyExportData(logs, monthStr);
      if (!byPositionMap.size) {
        message.info('该月份暂无符合条件（非总监/经理/系统管理员）的日志数据');
        return;
      }
      byPositionMap.forEach((list, position) => {
        exportPositionCsv(position, list, monthStr);
      });
      message.success('日志统计表已按职位分类导出');
      setExportModalVisible(false);
    } catch (error) {
      console.error('导出日志统计表失败:', error);
      message.error('导出失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  };

  /**
   * 函数：handleCancelExport 关闭导出弹窗并重置状态
   */
  const handleCancelExport = () => {
    if (exporting) return;
    setExportModalVisible(false);
  };

  /**
   * 函数：applySelectionAnimation 触发人员选择组件的平滑动画效果
   * 说明：在切换选中人员时短暂放大与淡入，提升视觉反馈
   */
  const applySelectionAnimation = () => {
    setAnimateSelect(true);
    setTimeout(() => setAnimateSelect(false), 220);
  };

  /**
   * 函数：ensureDefaultDesigner 在进入按设计师模式或数据变化时确保有一个默认选中用户
   * 说明：如果当前未选中或不在列表中，自动选择列表第一个
   */
  useEffect(() => {
    if (mode !== 'byUser') return;
    if (!designerIds.length) return;
    if (!selectedUserId || !designerIds.includes(selectedUserId)) {
      setSelectedUserId(designerIds[0]);
    }
  }, [mode, designerIds]);

  /**
   * 函数：handleShiftKey 监听Shift键按下与释放
   * 说明：按下Shift时开启滚轮切换模式；释放后停止响应滚轮切换
   */
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Shift') setIsShiftPressed(true); };
    const onKeyUp = (e) => { if (e.key === 'Shift') setIsShiftPressed(false); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  /**
   * 函数：handleWheelSwitchDesigner 在按住Shift且按设计师模式下，根据滚轮方向切换选中设计师
   * 说明：向上滚动切换到上一个、向下滚动到下一个；支持首尾循环，不阻止页面正常滚动
   */
  useEffect(() => {
    const onWheel = (e) => {
      if (!isShiftPressed) return;
      if (mode !== 'byUser') return;
      if (!designerIds.length) return;
      const total = designerIds.length;
      const currentIdx = designerIds.indexOf(selectedUserId);
      const baseIdx = currentIdx >= 0 ? currentIdx : (e.deltaY < 0 ? total - 1 : 0);
      const nextIdx = (baseIdx + (e.deltaY > 0 ? 1 : -1) + total) % total;
      const nextId = designerIds[nextIdx];
      if (nextId && nextId !== selectedUserId) {
        setSelectedUserId(nextId);
        applySelectionAnimation();
      }
      // 不调用preventDefault，保证页面可正常滚动
    };
    window.addEventListener('wheel', onWheel, { passive: true });
    return () => { window.removeEventListener('wheel', onWheel); };
  }, [isShiftPressed, mode, designerIds, selectedUserId]);

  // 按日期模式：查询当天所有日志（为便于统计，拉取较大条数）
  const { data: logsByDateResp, isLoading: loadingLogsByDate } = useQuery(
    ['dailyLogsByDate', selectedDate?.format('YYYY-MM-DD')],
    async () => {
      const date = selectedDate?.format('YYYY-MM-DD');
      const res = await axios.get('/daily-logs', {
        params: {
          startDate: date,
          endDate: date,
          page: 1,
          limit: 1000
        }
      });
      return res.data?.data || {};
    },
    { enabled: isAuthenticated && mode === 'byDate' && !!selectedDate }
  );

  const logsByDate = logsByDateResp?.logs || [];

  // 将当天日志按用户聚合，便于判断空缺与汇总工时
  const byDateTableData = useMemo(() => {
    const map = new Map();
    for (const log of logsByDate) {
      const uid = log.userId;
      if (!map.has(uid)) map.set(uid, []);
      map.get(uid).push(log);
    }
    return designers.map(u => {
      const list = map.get(u.id) || [];
      const totalHours = list.reduce((sum, l) => sum + Number(l.workHours || 0), 0);
      const latest = list[0];
      const status = getDailyStatusFromLogs(list);
      return {
        key: u.id,
        userId: u.id,
        realName: u.realName,
        position: u.position,
        status,
        totalHours: Number(totalHours || 0),
        logCount: list.length,
        latestSnippet: latest?.todayWork ? (latest.todayWork.length > 30 ? latest.todayWork.slice(0, 30) + '…' : latest.todayWork) : '',
        latestTomorrowPlan: latest?.tomorrowPlan ? (latest.todayWork.length > 30 ? latest.tomorrowPlan.slice(0, 30) + '…' : latest.tomorrowPlan) : '',
        logs: list
      };
    });
  }, [designers, logsByDate]);

  // 按设计师模式：按人员 + 日期范围查询日志
  const { data: logsByUserResp, isLoading: loadingLogsByUser } = useQuery(
    ['dailyLogsByUser', selectedUserId, dateRange?.[0]?.format('YYYY-MM-DD'), dateRange?.[1]?.format('YYYY-MM-DD')],
    async () => {
      if (!selectedUserId || !dateRange?.[0] || !dateRange?.[1]) return { logs: [], total: 0 };
      const res = await axios.get('/daily-logs', {
        params: {
          userId: selectedUserId,
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
          page: 1,
          limit: 1000
        }
      });
      return res.data?.data || {};
    },
    { enabled: isAuthenticated && mode === 'byUser' }
  );

  const logsByUser = logsByUserResp?.logs || [];

  // 按月统计模式：查询整月所有日志
  const { data: logsByMonthResp, isLoading: loadingLogsByMonth } = useQuery(
    ['dailyLogsByMonth', selectedMonth?.format('YYYY-MM')],
    async () => {
      const startDate = selectedMonth?.startOf('month').format('YYYY-MM-DD');
      const endDate = selectedMonth?.endOf('month').format('YYYY-MM-DD');
      const res = await axios.get('/daily-logs', {
        params: {
          startDate,
          endDate,
          page: 1,
          limit: 10000
        }
      });
      return res.data?.data || {};
    },
    { enabled: isAuthenticated && mode === 'byMonth' && !!selectedMonth }
  );

  const logsByMonth = logsByMonthResp?.logs || [];

  // 按月统计：将整月日志按用户聚合，统计工作日数、总工时等
  const byMonthTableData = useMemo(() => {
    const map = new Map();
    for (const log of logsByMonth) {
      const uid = log.userId;
      if (!map.has(uid)) map.set(uid, []);
      map.get(uid).push(log);
    }
    
    return designers.map(u => {
      const list = map.get(u.id) || [];
      const totalHours = list.reduce((sum, l) => sum + Number(l.workHours || 0), 0);
      const workDays = new Set(list.map(l => l.logDate)).size; // 去重计算工作日数
      const avgHours = workDays > 0 ? totalHours / workDays : 0;
      
      // 计算本月工作日总数（排除周末）
      const monthStart = selectedMonth?.startOf('month');
      const monthEnd = selectedMonth?.endOf('month');
      let totalWorkDays = 0;
      let cursor = monthStart?.clone();
      while (cursor && cursor.isSameOrBefore(monthEnd, 'day')) {
        const dayOfWeek = cursor.day();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) { // 排除周日(0)和周六(6)
          totalWorkDays++;
        }
        cursor = cursor.add(1, 'day');
      }
      
      const attendanceRate = totalWorkDays > 0 ? (workDays / totalWorkDays * 100) : 0;
      
      return {
        key: u.id,
        userId: u.id,
        realName: u.realName,
        position: u.position,
        workDays,
        totalWorkDays,
        attendanceRate: Number(attendanceRate || 0),
        totalHours: Number(totalHours || 0),
        avgHours: Number(avgHours || 0),
        logCount: list.length
      };
    });
  }, [designers, logsByMonth, selectedMonth]);

  // 表格列定义
  // 提取日志图片与聚合工具
  /**
   * 从单条日志中提取图片URL数组
   * 支持字段：imageUrls(数组或JSON字符串)、imageUrl(单字符串)
   * @param {Object} log 日志对象
   * @returns {string[]} 图片URL列表
   */
  const extractImagesFromLog = (log) => {
    if (!log) return [];
    if (Array.isArray(log.imageUrls)) return log.imageUrls.filter(Boolean);
    if (typeof log.imageUrls === 'string' && log.imageUrls.trim()) {
      try {
        const arr = JSON.parse(log.imageUrls);
        if (Array.isArray(arr)) return arr.filter(Boolean);
      } catch (e) {}
    }
    return log.imageUrl ? [log.imageUrl] : [];
  };

  /**
   * 通过日志列表聚合并去重图片URL
   * @param {Object[]} logs 日志列表
   * @returns {string[]} 去重后的图片URL列表
   */
  const collectImagesFromLogs = (logs = []) => {
    const set = new Set();
    for (const l of logs) {
      const imgs = extractImagesFromLog(l);
      imgs.forEach(u => { if (u) set.add(u); });
    }
    return Array.from(set);
  };
  const byDateColumns = [
    { title: '设计师', dataIndex: 'realName', key: 'realName', width: 100, render: (name, record) => (
      <Typography.Link onClick={() => handleSwitchToUser(record.userId, 'byDate')}>{name}</Typography.Link>
    ) },
    { title: '岗位', dataIndex: 'position', key: 'position', width: 90 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v) => {
        if (v === '空缺') return <Text type="danger">空缺</Text>;
        if (v === '调休') return <Tag color="blue">调休</Tag>;
        return <Tag color="green">有日志</Tag>;
      }
    },
    { title: '日志条数', dataIndex: 'logCount', key: 'logCount', width: 90 },
    { title: '工时合计', dataIndex: 'totalHours', key: 'totalHours', width: 100, render: (v) => (Number(v) || 0).toFixed(1) },
    { title: '内容摘要', dataIndex: 'latestSnippet', key: 'latestSnippet', width: 320 },
    { title: '明日计划', dataIndex: 'latestTomorrowPlan', key: 'latestTomorrowPlan', width: 320 },
    {
      title: '图片',
      dataIndex: 'logs',
      key: 'thumbnails',
      width: 180,
      render: (logs) => {
        const imgs = collectImagesFromLogs(logs);
        if (!imgs || imgs.length === 0) return '-';
        const visible = imgs.slice(0, 3);
        const hidden = imgs.slice(3);
        return (
          <Image.PreviewGroup>
            <Space wrap size={8}>
              {visible.map((src, idx) => (
                <Image
                  key={idx}
                  src={src}
                  width={48}
                  height={48}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              ))}
              {hidden.map((src, idx) => (
                <Image key={`hidden-${idx}`} src={src} style={{ display: 'none' }} />
              ))}
            </Space>
          </Image.PreviewGroup>
        );
      }
    }
  ];

  const byUserColumns = [
    { title: '日期', dataIndex: ['logDate'], key: 'logDate', width: 110, render: (v) => v || '-' },
    { title: '项目', dataIndex: ['task', 'project', 'name'], key: 'project', width: 140, render: (v) => v || '-' },
    { title: '任务', dataIndex: ['task', 'title'], key: 'task', width: 140, render: (v) => v || '-' },
    { title: '工时', dataIndex: 'workHours', key: 'workHours', width: 80, render: (v) => (Number(v) || 0).toFixed(1) },
    { title: '工作内容', dataIndex: 'todayWork', key: 'todayWork', width: 200, render: (v) => v || '-' },
    { title: '明日计划', dataIndex: 'tomorrowPlan', key: 'tomorrowPlan', width: 300, render: (v) => v || '-' },
    {
      title: '图片',
      dataIndex: 'imageUrls',
      key: 'thumbnails',
      width: 180,
      render: (v, record) => {
        const imgs = extractImagesFromLog(record);
        if (!imgs || imgs.length === 0) return '-';
        const visible = imgs.slice(0, 3);
        const hidden = imgs.slice(3);
        return (
          <Image.PreviewGroup>
            <Space wrap size={8}>
              {visible.map((src, idx) => (
                <Image
                  key={idx}
                  src={src}
                  width={48}
                  height={48}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
              ))}
              {hidden.map((src, idx) => (
                <Image key={`hidden-${idx}`} src={src} style={{ display: 'none' }} />
              ))}
            </Space>
          </Image.PreviewGroup>
        );
      }
    }
  ];

  const byMonthColumns = [
    { title: '设计师', dataIndex: 'realName', key: 'realName', width: 100, render: (name, record) => (
      <Typography.Link onClick={() => handleSwitchToUser(record.userId, 'byMonth')}>{name}</Typography.Link>
    ) },
    { title: '岗位', dataIndex: 'position', key: 'position', width: 90 },
    { title: '工作日数', dataIndex: 'workDays', key: 'workDays', width: 110 },
    { title: '应工作日数', dataIndex: 'totalWorkDays', key: 'totalWorkDays', width: 120 },
    { 
      title: '出勤率', 
      dataIndex: 'attendanceRate', 
      key: 'attendanceRate', 
      width: 100,
      render: (v) => {
        const rate = Number(v) || 0;
        const color = rate >= 90 ? 'green' : rate >= 80 ? 'orange' : 'red';
        return <Tag color={color}>{rate.toFixed(1)}%</Tag>;
      }
    },
    { title: '总工时', dataIndex: 'totalHours', key: 'totalHours', width: 110, render: (v) => (Number(v) || 0).toFixed(1) },
    { title: '日均工时', dataIndex: 'avgHours', key: 'avgHours', width: 110, render: (v) => (Number(v) || 0).toFixed(1) },
    { title: '日志条数', dataIndex: 'logCount', key: 'logCount', width: 100 }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        

        <Card
          title="日志统计"
          extra={
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleOpenExportModal}
            >
              导出日志统计表
            </Button>
          }
        >
          <Space size={[16, 12]} wrap align="center">
            <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
              <Radio.Button value="byDate">按日期</Radio.Button>
              <Radio.Button value="byUser">按设计师</Radio.Button>
              <Radio.Button value="byMonth">按月统计</Radio.Button>
            </Radio.Group>

            {mode === 'byDate' && (
              <Space align="center">
                <Text type="secondary">日期</Text>
                <DatePicker
                  value={selectedDate}
                  onChange={(v) => setSelectedDate(v || dayjs())}
                  allowClear={false}
                />
              </Space>
            )}

            {mode === 'byMonth' && (
              <Space align="center">
                <Text type="secondary">月份</Text>
                <DatePicker
                  picker="month"
                  value={selectedMonth}
                  onChange={(v) => setSelectedMonth(v || dayjs())}
                  allowClear={false}
                  format="YYYY-MM"
                />
              </Space>
            )}

            {mode === 'byUser' && (
              <>
                <Space align="center">
                  <Text type="secondary">设计师</Text>
                  <Select
                    showSearch
                    style={{ 
                      width: 220,
                      transition: 'transform 0.22s ease, box-shadow 0.22s ease, opacity 0.22s ease',
                      transform: animateSelect ? 'scale(1.02)' : 'none',
                      boxShadow: animateSelect ? '0 0 0 2px rgba(24,144,255,0.35)' : 'none',
                      opacity: animateSelect ? 0.95 : 1
                    }}
                    placeholder={loadingUsers ? '加载中…' : '请选择设计师'}
                    loading={loadingUsers}
                    optionFilterProp="label"
                    value={selectedUserId}
                    onChange={setSelectedUserId}
                    allowClear
                    options={designers.map(u => ({ value: u.id, label: u.realName }))}
                  />
                </Space>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>按住 Shift + 滚轮 可在设计师间循环切换</Text>
                <Space align="center">
                  <Text type="secondary">日期范围</Text>
                  <RangePicker
                    value={dateRange}
                    onChange={(v) => setDateRange(v || [dayjs().subtract(6, 'day'), dayjs()])}
                    allowClear={false}
                  />
                </Space>
              </>
            )}
          </Space>
        </Card>

        {mode === 'byDate' ? (
          <Card title={`按日期统计（${selectedDate?.format('YYYY-MM-DD')}）`}>
            <Table
              loading={loadingUsers || loadingLogsByDate}
              dataSource={byDateTableData}
              columns={byDateColumns}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              locale={{ emptyText: <Empty description="暂无数据" /> }}
              rowKey="key"
            />
          </Card>
        ) : mode === 'byMonth' ? (
          <Card title={`按月统计（${selectedMonth?.format('YYYY年MM月')}）`}>
            <Table
              loading={loadingUsers || loadingLogsByMonth}
              dataSource={byMonthTableData}
              columns={byMonthColumns}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              locale={{ emptyText: <Empty description="暂无数据" /> }}
              rowKey="key"
            />
          </Card>
        ) : (
          <Card title="按设计师统计">
            {(!selectedUserId || !dateRange?.[0] || !dateRange?.[1]) ? (
              <Empty description="请选择设计师并设置日期范围" />
            ) : (
              <Table
                loading={loadingLogsByUser}
                dataSource={logsByUser}
                columns={byUserColumns}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                locale={{ emptyText: <Empty description="无日志" /> }}
                rowKey={(r) => r.id}
              />
            )}
          </Card>
        )}
        <Modal
          title="导出日志统计表"
          open={exportModalVisible}
          onOk={handleConfirmExport}
          onCancel={handleCancelExport}
          confirmLoading={exporting}
          okText={exporting ? '正在导出...' : '开始导出'}
          cancelText="取消"
          maskClosable={!exporting}
          destroyOnClose
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Text type="secondary">
              请选择需要导出的月份，系统将统计当月每位成员的日志数量，
              并按“职位”拆分为多个CSV文件下载（不包含总监、各部门经理、系统管理员）。
            </Text>
            <Space align="center">
              <Text type="secondary">导出月份</Text>
              <DatePicker
                picker="month"
                value={exportMonth}
                onChange={(v) => setExportMonth(v || dayjs())}
                allowClear={false}
                format="YYYY-MM"
              />
            </Space>
          </Space>
        </Modal>
      </Space>
    </div>
  );
}
